/**
 * Shared date/time formatting. These exact `toLocale*` patterns were
 * copy-pasted across ~19 page loaders and components; centralise them so the
 * format is consistent and changes land in one place. UK English throughout.
 */

type DateInput = Date | string | number;

function toDate(d: DateInput): Date {
  return d instanceof Date ? d : new Date(d);
}

/**
 * 🚨 The server and the phone spell ONE month differently, and it threw away
 * every page.
 *
 * CLDR 42 renamed `en-GB`'s abbreviated September from "Sep" to "Sept". Node's
 * bundled ICU has that change; Apple's system ICU — which is what WebKit uses,
 * so every iPhone, every iPad, Safari on a Mac, and the Mucka app's WKWebView —
 * still says "Sep". Measured on 2026-09-21, Node 22 against
 * `JavaScriptCore.framework`, `en-GB` + `Europe/London`:
 *
 *   Node   : 14 Jan … 14 Aug · **14 Sept** · 14 Oct …
 *   WebKit : 14 Jan … 14 Aug · **14 Sep**  · 14 Oct …
 *
 * September is the ONLY month that differs, and `month: 'short'` (and
 * `dateStyle: 'medium'`, which uses it) is the only option that exposes it.
 * Weekdays, long months, numeric dates and every time format agree exactly —
 * but a short month WITH a time is also joined differently, in every month
 * (see `agreeDateTimeJoin` below).
 *
 * That one character is a hydration mismatch. React does not patch a text
 * mismatch at the root of the document — it throws the whole tree away and
 * client-renders it: in the Sentry replays for MUCKA-WEB-5 the recovery removes
 * all fifteen `<head>` nodes plus every child of `<body>` and re-inserts 500-900
 * of them, 16-36ms after load, on every page an engineer opens. The event count
 * went from ~4/day in late August to ~48/day on 1 September — a 12x step on the
 * calendar boundary, not on a deploy — and 94% of the events are WebKit clients.
 * It would have healed itself on 1 October and come back next September.
 *
 * So: never hand an `en-GB` short month straight to the DOM. `ukDate` below is
 * the way to format one; `agreeShortMonth` is the single place the two engines
 * are reconciled, and it reconciles to "Sep" because that is what the majority
 * of our users' devices already show and what every doc comment here promises.
 *
 * Every module in the CLIENT BUNDLE now formats a short month through here —
 * enforced by `a-date-must-read-the-same-on-both-sides-of-hydration.test.ts`,
 * which walks the `'use client'` import graph rather than a list of folders.
 *
 * 🔴 The 42 sites left outside it are SERVER-ONLY — API routes, crons, engine
 * context, e-mail and WhatsApp bodies. They render once and are never hydrated,
 * so they cannot produce this fault, and changing them would be a copy change
 * to messages customers have already received rather than a bug fix. Two more
 * are named in `DOCUMENTS_NOT_SCREENS` in that test, with their reasons.
 */
const CLDR_42_SHORT_SEPTEMBER = /\bSept\b/g;

/**
 * Pin the one abbreviation Node's ICU and Apple's ICU disagree about, so a
 * date reads the same on both sides of hydration. Safe on any string: "Sept"
 * only ever reaches here as a formatted month.
 */
export function agreeShortMonth(formatted: string): string {
  return formatted.replace(CLDR_42_SHORT_SEPTEMBER, 'Sep');
}

/**
 * 🚨 The second thing a short month splits: how the date is joined to a time.
 *
 * With `month: 'short'` (or `dateStyle: 'medium'`) and a time, Node and Chrome
 * print "14 Oct, 10:05" and WebKit prints "14 Oct at 10:05" — in EVERY month,
 * not only September. Measured 20 Oct 2026, Node 22.15 / Chrome 155 against
 * JavaScriptCore on macOS 26.6. A long month ("14 October at 10:05") and a
 * numeric date ("14/10, 10:05") read the same on both, so they are left alone.
 * An iPhone threw /messages away over this (MUCKA-WEB-A2).
 *
 * Pinned to the comma: it is what the server prints, and what every Android
 * and desktop screen already shows. The lookahead is a time ("10:05"), so a
 * `dayPeriod` reading "at night" is never touched.
 *
 * On the `format()` string, deliberately not rebuilt from `formatToParts()`:
 * V8 keeps a narrow no-break space before "am" in the parts and prints a plain
 * one from `format()`, so a joined-up string would be a third spelling.
 */
const WEBKIT_DATE_TIME_JOIN = / at (?=\d{1,2}:\d{2})/;

function agreeDateTimeJoin(formatted: string, options: Intl.DateTimeFormatOptions): string {
  const shortMonth = options.month === 'short' || options.dateStyle === 'medium';
  return shortMonth ? formatted.replace(WEBKIT_DATE_TIME_JOIN, ', ') : formatted;
}

/**
 * Format a date for a UK screen. Use this instead of `toLocaleDateString`,
 * `toLocaleString` or a bare `Intl.DateTimeFormat` anywhere the output can be
 * server-rendered and then hydrated — which is every component in this app.
 *
 * Renders exactly the fields you ask for, so it covers a date, a date and a
 * time, or a month and a year. `Intl.DateTimeFormat` rather than
 * `toLocaleDateString` for that last reason: measured, the two agree on every
 * option set this app passes, and part company on one — time-only options,
 * where `toLocaleDateString` bolts on a date nobody asked for.
 *
 * Defaults to `Europe/London` (anti-pattern #9); pass `timeZone` to override —
 * several callers legitimately want `UTC` for a date-only column.
 */
export function ukDate(d: DateInput, options: Intl.DateTimeFormatOptions): string {
  return agreeShortMonth(agreeDateTimeJoin(
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', ...options }).format(toDate(d)),
    options,
  ));
}

/**
 * A calendar day ("2026-10-12", a `date` column) as a Date that means the SAME
 * day on the server, in London and on the phone in someone's hand.
 *
 * 🚨 `new Date('2026-10-12T00:00:00')` — no zone — is midnight in the DEVICE's
 * zone. Formatted in `Europe/London`, as `ukDate` does, that is the right day
 * on the server (UTC) and in the UK, and the PREVIOUS day on any device two or
 * more hours east: Paris, Madrid, Brussels, Istanbul — midnight there is 23:00
 * the evening before in London. So the server wrote "12 Oct", the phone wrote
 * "11 Oct", and React threw the whole page away (MUCKA-WEB-5, 22-26 Sep 2026:
 * one firm's engineers abroad on the Schedule, two others' office staff on
 * Invoices, all on UTC+2/+3 device clocks). The owner then read the wrong due
 * date, because the client render is the one that stays on screen.
 *
 * Noon UTC is the same calendar day in every zone from UTC-11 to UTC+11, so
 * London formatting AND the device's own `getDate()` both read the day that
 * was stored. Only for a DATE — a timestamp is an instant and goes straight to
 * `ukDate`; its UTC date part is not its London day.
 */
export function calendarDate(ymd: string): Date {
  return new Date(`${ymd.slice(0, 10)}T12:00:00Z`);
}

/** "Monday, 8 June" — the page-header date label. Defaults to today. */
export function formatDateLabel(d: DateInput = new Date()): string {
  return toDate(d).toLocaleDateString('en-GB', { timeZone: 'Europe/London', weekday: 'long', day: 'numeric', month: 'long' });
}

/** "8 Jun 2026" — compact date. Empty string for unscheduled (null) input. */
export function formatDate(d: DateInput | null | undefined): string {
  if (d == null) return '';
  return ukDate(d, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "Mon, 8 Jun" — short weekday + date. Empty string for null input. */
export function formatShortDate(d: DateInput | null | undefined): string {
  if (d == null) return '';
  return ukDate(d, { weekday: 'short', day: 'numeric', month: 'short' });
}

/** "3pm" / "3:30pm" — lowercase 12-hour time, no space before am/pm.
 *  Empty string for unscheduled (null) input. */
export function formatTime(d: DateInput | null | undefined): string {
  if (d == null) return '';
  return toDate(d)
    .toLocaleTimeString('en-GB', { timeZone: 'Europe/London', hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(' ', '')
    .toLowerCase();
}

/** "just now" / "5 min ago" / "3 hours ago" / "2 days ago". */
export function formatRelativeTime(d: DateInput, now: DateInput = new Date()): string {
  const diffMs = toDate(now).getTime() - toDate(d).getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** "in_progress" -> "In Progress". Humanise a snake/kebab status slug. */
export function formatStatusLabel(status: string): string {
  return status.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
